import { createContext, useContext, type ReactNode } from 'react'
import type { WorkspaceEvent } from '../workspace/activity'
import type { AccountPins } from './accountPins'

/** A live fact about a chat (the Workspace socket's event, ADR 0022). */
export type LiveEvent = WorkspaceEvent

export interface LiveNotification {
  title: string
  body: string
  tag: string
  /** The in-app page the notification is about (the Desktop client opens it on click). */
  href?: string
}

/**
 * What the Chats page needs beyond the Library's gateway and media (ADR 0024):
 * CRM-local routes (AI overview, buying power, fan insights, hand-off-free
 * extras) and a clock. The console provides the real ones; tests provide fakes.
 */
export interface ChatsDeps {
  /** Call a CRM-local route (`/v1/...` served by the CRM itself; POST unless said); the `data` of its envelope. */
  local: <T = unknown>(path: string, body: Record<string, unknown>, method?: 'GET' | 'POST' | 'PUT') => Promise<T>
  now: () => Date
  /** `<crm user>::<account>`: the CRM's browser-storage scope, so names carry over from the old page. */
  storageScope: string
  /** The person's Pinned chats on this Account (ADR 0035); left out, pins live in memory. */
  pins?: AccountPins
  /** Live events for one account; returns the unsubscribe. */
  subscribe: (accountId: string, fn: (ev: LiveEvent) => void) => () => void
  /** The account's events of the last hour, again whenever they change (the Activity tab); returns the unsubscribe. */
  history?: (accountId: string, fn: (events: LiveEvent[]) => void) => () => void
  /** Chime + desktop notification. */
  notify: (n: LiveNotification) => void
  /** Warm a chat's newest messages before it is opened (hover). */
  prefetch?: (fanId: string) => void
}

const Ctx = createContext<ChatsDeps | null>(null)

export function ChatsDepsProvider({ value, children }: { value: ChatsDeps; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useChatsDeps(): ChatsDeps {
  const deps = useContext(Ctx)
  if (!deps) throw new Error('useChatsDeps outside ChatsDepsProvider')
  return deps
}
